import clsx from "clsx";
import { BarChart3, Table2 } from "lucide-react";
import { useState, type ReactNode } from "react";

export interface LegendItem {
  label: string;
  color: string;
  kind?: "rect" | "line";
}

/**
 * Chart container: title, legend (for 2+ series), and the table-view twin that makes every
 * value reachable without hovering or seeing colour.
 */
export function ChartFrame({ title, subtitle, legend, table, children, className, loading }: {
  title: string;
  subtitle?: ReactNode;
  legend?: LegendItem[];
  table: ReactNode;
  children: ReactNode;
  className?: string;
  loading?: boolean;
}) {
  const [showTable, setShowTable] = useState(false);
  return (
    <figure className={clsx("rounded-xl border border-border bg-surface p-4", className)}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <figcaption>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {subtitle && <div className="mt-0.5 text-xs text-ink-3">{subtitle}</div>}
        </figcaption>
        <button
          type="button"
          onClick={() => setShowTable((s) => !s)}
          aria-pressed={showTable}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-ink-3 hover:bg-surface-2 hover:text-ink"
        >
          {showTable ? <BarChart3 className="size-3.5" aria-hidden /> : <Table2 className="size-3.5" aria-hidden />}
          {showTable ? "Chart" : "Table"}
        </button>
      </div>
      {legend && legend.length >= 2 && !showTable && (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
          {legend.map((l) => (
            <li key={l.label} className="inline-flex items-center gap-1.5">
              {l.kind === "line" ? (
                <span className="h-0.5 w-3 rounded" style={{ background: l.color }} aria-hidden />
              ) : (
                <span className="size-2.5 rounded-sm" style={{ background: l.color }} aria-hidden />
              )}
              {l.label}
            </li>
          ))}
        </ul>
      )}
      <div className={clsx(loading && "opacity-60")}>{showTable ? table : children}</div>
    </figure>
  );
}

export function DataTable({ columns, rows }: {
  columns: { key: string; label: string; align?: "left" | "right" }[];
  rows: Record<string, ReactNode>[];
}) {
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-surface text-xs text-ink-3">
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={clsx("px-2 py-1.5 font-medium", c.align === "right" ? "text-right" : "text-left")}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-line">
              {columns.map((c) => (
                <td key={c.key} className={clsx("px-2 py-1.5", c.align === "right" ? "num text-right" : "text-ink-2")}>
                  {r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Tooltip body: values lead, labels follow; series keyed by a short line, not a box. */
export function TooltipCard({ title, rows }: {
  title: string;
  rows: { label: string; value: string; color: string }[];
}) {
  return (
    <div className="min-w-40 rounded-lg border border-border bg-surface px-3 py-2 shadow-lg">
      <p className="mb-1 text-xs text-ink-3">{title}</p>
      <ul className="flex flex-col gap-1">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-2">
            <span className="h-0.5 w-3 shrink-0 rounded" style={{ background: r.color }} aria-hidden />
            <span className="num text-sm font-semibold text-ink">{r.value}</span>
            <span className="text-xs text-ink-3">{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
